'use client';

import { ReactNode, useMemo, useRef, useState } from 'react';
import { UserSettings } from '../../../environmentVariables/EnvironmentVariables';
import {
	BOOSTED_MULITPLIER,
	TURBO_MULTIPLIER,
} from '../../../providers/priorityFees/consts';
import usePriorityFeeStore from '../../../stores/usePriorityFeeStore';
import {
	PriorityFeeCalculator,
	SANITY_CHECK_ABS_MAX_FEE_IN_SOL,
} from '../../../utils/PriorityFeeCalculator';
import Input from '../../primitives/Input/Input';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { MarketId } from '@drift/common';
import MarketIcon from 'src/components/Utils/MarketIcon';
import { LAMPORTS_PER_SOL } from '@solana/web3.js';
import { LAMPORTS_EXP } from '@drift-labs/sdk';
import { Settings } from 'src/components/Settings/SettingsForm';
import { getRadioItemCardClassName } from 'src/components/primitives/RadioGroup/utils';

type PriorityFeeInputProps = {
	value: UserSettings['priorityFee'];
	selectedValue: UserSettings['priorityFee'];
	label: string;
	description?: string | ReactNode;
	onSelect: () => void;
	getPriorityFeeEstimateInSol: () => number;
};

const lamportsPerSol = LAMPORTS_PER_SOL;
const lamportsPrecisionExp = LAMPORTS_EXP.toNumber();
const MIN_SOL_STR = (1 / lamportsPerSol).toFixed(lamportsPrecisionExp);

const PriorityFeeInputButton = (props: PriorityFeeInputProps) => {
	const isSelected = props.selectedValue === props.value;

	return (
		<button
			className={getRadioItemCardClassName({
				size: 'md',
				className: 'flex-1 flex-col items-start justify-start gap-y-sm',
			})}
			data-state={isSelected ? 'checked' : 'unchecked'}
			onClick={props.onSelect}
		>
			<span className="typo-t3 text-text-default">{props.label}</span>
			<span className="text-text-secondary transition-colors [[data-state=checked]_&]:text-text-default">
				{props.description}
			</span>
		</button>
	);
};

const PriorityFeeSettingsInputs = ({
	localSettings,
	setLocalSettings,
}: {
	localSettings: Settings;
	setLocalSettings: (setting: Settings) => void;
}) => {
	const initialSettingsRef = useRef(localSettings);
	const currentSettingsRef = useRef(localSettings);

	const getPriorityFeeToUse = usePriorityFeeStore((s) => s.getPriorityFeeToUse);

	const getOraclePrice = useGetOraclePriceForMarket();

	const localPriorityFeeSetting = useRef(localSettings.priorityFee);

	const localCustomPriorityFee = useRef<string>(
		localSettings.customPriorityFee
			?.toFixed(lamportsPrecisionExp) // Use toFixed to prevent small numbers being formatted as scientific notation
			.replace(/\.?0+$/, '') // Trim any trailing zeros
	);

	const localPriorityFeeMaxCap = useRef(
		localSettings?.maxPriorityFeeCap?.toString()
	);

	const [showMaxFeeMessage, setShowMaxFeeMessage] = useState(false);

	const solMarketId = MarketId.createPerpMarket(0);

	const updateSettingsValue = (updatedSettings: Partial<UserSettings>) => {
		const newSettings = {
			...currentSettingsRef.current,
			...updatedSettings,
		};

		setLocalSettings(newSettings);

		currentSettingsRef.current = newSettings;
	};

	const getPriorityFeeEstimateInSol = (
		setting: UserSettings['priorityFee']
	) => {
		return PriorityFeeCalculator.getPriorityFeeInSolForComputeUnitPrice(
			getPriorityFeeToUse(setting)
		);
	};

	const getPriorityFeeEstimateInUsd = (setting: UserSettings['priorityFee']) =>
		convertSolToUsdString(getPriorityFeeEstimateInSol(setting));

	const convertSolToUsdString = (solAmount: number) => {
		const usdAmount = solAmount * (getOraclePrice(solMarketId)?.toNum() ?? 0);

		if (isNaN(usdAmount)) {
			return '';
		} else if (usdAmount < 0.01) {
			return '<$0.01';
		} else {
			return `~$${usdAmount.toFixed(2)}`;
		}
	};

	const priorityFeeButtonOptions: Pick<
		PriorityFeeInputProps,
		'value' | 'label' | 'description'
	>[] = useMemo(
		() => [
			{
				value: 'boosted',
				label: 'Boosted',
				description: `${BOOSTED_MULITPLIER}x`,
			},
			{
				value: 'turbo',
				label: 'Turbo',
				description: `${TURBO_MULTIPLIER}x`,
			},
			{
				value: 'custom',
				label: 'Custom',
			},
		],
		[]
	);

	const showMaxLowerThanEstMessage = useMemo(
		() =>
			localPriorityFeeSetting.current !== 'custom' &&
			parseFloat(localPriorityFeeMaxCap.current) <
				getPriorityFeeEstimateInSol(localPriorityFeeSetting.current),
		[localPriorityFeeMaxCap.current, localPriorityFeeSetting.current]
	);

	const handleCustomPriorityFeeChange = (value: string) => {
		try {
			let valueNum = +value;
			if (!isNaN(valueNum) && valueNum >= 0) {
				if (valueNum > SANITY_CHECK_ABS_MAX_FEE_IN_SOL) {
					setShowMaxFeeMessage(true);

					updateSettingsValue({
						priorityFee: 'custom',
						customPriorityFee: SANITY_CHECK_ABS_MAX_FEE_IN_SOL,
					});

					localPriorityFeeSetting.current = 'custom';
					localCustomPriorityFee.current = `${SANITY_CHECK_ABS_MAX_FEE_IN_SOL}`;
				} else {
					// manually enforce 1 lamport as minimum size since 1e-9 has issues with generic component
					const decimalPlaces = value?.split('.')?.[1]?.length ?? 0;
					if (decimalPlaces > lamportsPrecisionExp) {
						value = MIN_SOL_STR;
						valueNum = +value;
					}
					updateSettingsValue({
						priorityFee: 'custom',
						customPriorityFee: valueNum,
					});

					localPriorityFeeSetting.current = 'custom';
					localCustomPriorityFee.current = value;
					setShowMaxFeeMessage(false);
				}
			} else {
				localCustomPriorityFee.current = '';
			}
		} catch (e) {
			return;
		}
	};

	const handleMaxPriorityFeeCapChange = (value: string) => {
		let valueNum = +value;

		if (!isNaN(valueNum) && valueNum >= 0) {
			if (valueNum > SANITY_CHECK_ABS_MAX_FEE_IN_SOL) {
				valueNum = SANITY_CHECK_ABS_MAX_FEE_IN_SOL;
				value = '1';
				setShowMaxFeeMessage(true);
			} else {
				setShowMaxFeeMessage(false);
			}

			// manually enforce 1 lamport as minimum size since 1e-9 has issues with generic component
			const decimalPlaces = value?.split('.')?.[1]?.length ?? 0;
			if (decimalPlaces > 9) {
				value = MIN_SOL_STR;
				valueNum = +value;
			}

			updateSettingsValue({
				maxPriorityFeeCap: valueNum,
				//priorityFee: localPriorityFeeSetting.current,
			});
			localPriorityFeeMaxCap.current = value;
		} else {
			localPriorityFeeMaxCap.current = '';
		}
	};

	return (
		<div className="flex flex-col gap-y-md">
			<div className="flex flex-col gap-y-sm">
				<h3 className="typo-t3 text-text-default">Priority Fee</h3>
				<p className="typo-b4 text-text-secondary">
					An extra fee added to your transactions to encourage Solana validators
					to process your transaction faster. Set a cap to prevent overpaying.
				</p>
			</div>

			<div className="grid grid-cols-2 gap-md sm:grid-cols-4">
				<PriorityFeeInputButton
					selectedValue={localPriorityFeeSetting.current}
					value="dynamic"
					label="Dynamic"
					description={
						<div className="flex items-center gap-x-1">
							<MarketIcon sizeClass="size-3" marketSymbol="SOL" />
							<span>{getPriorityFeeEstimateInSol('dynamic')?.toFixed(5)}</span>
						</div>
					}
					onSelect={() => {
						updateSettingsValue({
							priorityFee: 'dynamic',
							customPriorityFee: undefined,
						});
						localPriorityFeeSetting.current = 'dynamic';
						localCustomPriorityFee.current = '';

						setShowMaxFeeMessage(false);
					}}
					getPriorityFeeEstimateInSol={() =>
						getPriorityFeeEstimateInSol('dynamic')
					}
				/>

				{priorityFeeButtonOptions.map((option) => {
					return (
						<PriorityFeeInputButton
							key={option.value}
							selectedValue={localPriorityFeeSetting.current}
							{...option}
							onSelect={() => {
								if (option.value === 'custom') {
									if (initialSettingsRef.current.customPriorityFee) {
										updateSettingsValue({
											priorityFee: option.value,
											customPriorityFee:
												initialSettingsRef.current.customPriorityFee,
										});
									} else {
										updateSettingsValue({
											priorityFee: option.value,
										});
									}

									localCustomPriorityFee.current =
										initialSettingsRef.current.customPriorityFee?.toString() ??
										'';
								} else {
									updateSettingsValue({
										priorityFee: option.value,
										customPriorityFee: undefined,
									});
									localCustomPriorityFee.current = '';
								}

								localPriorityFeeSetting.current = option.value;
								setShowMaxFeeMessage(false);
							}}
							getPriorityFeeEstimateInSol={() =>
								getPriorityFeeEstimateInSol(option.value)
							}
						/>
					);
				})}
			</div>

			{localPriorityFeeSetting.current === 'dynamic' && (
				<p className="typo-b4 text-text-secondary">
					Automatically adjusts, targeting the 75th percentile of fees in the
					last 10 blocks.
				</p>
			)}

			{localPriorityFeeSetting.current === 'custom' ? (
				<Input
					id="custom-priority-fee"
					type="number"
					labelVariant="standard"
					label="Custom Fee"
					value={localCustomPriorityFee.current?.toString()}
					onChange={handleCustomPriorityFeeChange}
					suffix="SOL"
				/>
			) : (
				<div className="flex w-full items-center justify-between gap-2">
					<Input
						id="est-fee"
						type="number"
						labelVariant="standard"
						label={
							<>
								<span>Est. Fee</span>
								<span>
									{getPriorityFeeEstimateInUsd(localPriorityFeeSetting.current)}
								</span>
							</>
						}
						labelClassName="flex justify-between"
						value={`${parseFloat(
							getPriorityFeeEstimateInSol(
								localPriorityFeeSetting.current
							)?.toFixed(9)
						)}`}
						onChange={() => {}}
						suffix="SOL"
						disabled
					/>

					<Input
						id="max-fee-cap"
						labelVariant="standard"
						type="number"
						label={
							<>
								<span>Max Fee Cap</span>
								<span>
									{convertSolToUsdString(
										parseFloat(localPriorityFeeMaxCap.current)
									)}
								</span>
							</>
						}
						labelClassName="flex justify-between"
						value={localPriorityFeeMaxCap.current}
						onChange={handleMaxPriorityFeeCapChange}
						suffix="SOL"
					/>
				</div>
			)}

			{showMaxFeeMessage && (
				<InlineAlert
					variant="warning"
					title={`Max fee is ${SANITY_CHECK_ABS_MAX_FEE_IN_SOL} SOL`}
				/>
			)}

			{showMaxLowerThanEstMessage && (
				<InlineAlert
					variant="warning"
					title="Max fee is less than current estimate. Transactions may time out."
				/>
			)}
		</div>
	);
};

export default PriorityFeeSettingsInputs;
